/**
 * Search Resource
 *
 * Thin 1:1 mapping to the search API. Ships one user endpoint
 * (POST /v1/search) plus a typed convenience wrapper for the
 * recent-browse case (recent() = empty query + updated_at_desc sort),
 * the admin cross-tenant path, reindex, and chunk ingest.
 *
 * There is no triggerReindex() — there is no
 * customer-facing admin role in v1; ops invoke BackfillAccountWorkflow
 * via Temporal client.
 *
 * There is no /v1/search/recent endpoint — recent() is a typed
 * helper that calls /v1/search with the recency sort.
 */

import type { HttpLayer } from '../http.js';
import type {
  SearchRequest,
  SearchResponse,
} from '../types/search.js';

export class SearchResource {
  constructor(private http: HttpLayer) {}

  /**
   * Run a search query against the authenticated account's index.
   * Any authenticated user with a valid JWT for the account may call
   * this — there is no per-role distinction in v1.
   */
  async search(req: SearchRequest): Promise<SearchResponse> {
    return this.http.post('/v1/search', req);
  }

  /**
   * Typed convenience wrapper for the recency-sorted browse view.
   * Calls /v1/search with the canonical "recent" parameters
   * (empty query, updated_at_desc, default limit). The helper narrows the contract for
   * callers; it is NOT a backwards-compat shim.
   */
  async recent(): Promise<SearchResponse> {
    return this.http.post('/v1/search', {
      query: '',
      sort: 'updated_at_desc',
      limit: 20,
    });
  }

  /**
   * Admin cross-tenant search. Admin-scoped;
   * INTENTIONALLY bypasses the mandatory org+account tenant filter, taking a
   * target organization_id/account_id (or none to span tenants). Used by the
   * tenancy suite to confirm another account's points DO exist in the shared
   * collection — proving the filter (not absence of data) hides them.
   */
  async adminSearch(
    req: SearchRequest & { organization_id?: string; account_id?: string },
  ): Promise<SearchResponse> {
    return this.http.post('/v1/admin/search', req);
  }

  /** Re-index the authenticated account (non-destructive; catalog from DB). */
  async reindex(req?: { scope?: 'all' | 'catalog' | 'files' }): Promise<{
    catalog: string;
    files: string;
  }> {
    return this.http.post('/v1/search/reindex', req ?? {});
  }

  /**
   * Ingest file-content chunks into the central tier.
   * Desktop path: account JWT + a body-supplied desktop-local
   * workspace_id (the endpoint authorizes by account_id). op defaults to upsert.
   */
  async ingestChunks(req: {
    workspace_id: string;
    assignment_epoch: number;
    op?:
      | 'upsert'
      | 'delete'
      | 'replace-file'
      | 'upsert-directories'
      | 'index-status';
    chunks: Array<{
      path: string;
      chunk_type?: string;
      content: string;
      symbols?: Array<{ name: string; kind: string; role: 'definition' | 'reference' }>;
      start_line?: number;
      end_line?: number;
    }>;
    directories?: Array<{
      path: string;
      parent_path?: string;
      name: string;
      updated_at?: string;
    }>;
    ready?: boolean;
    indexed?: number;
    queued?: number;
    embedding?: number;
    health?: 'ok' | 'degraded';
    wait_paths?: string[];
  }): Promise<{ accepted: number; directories_accepted?: number; op: string }> {
    return this.http.post('/v1/search/chunks', req);
  }
}
